import type { DisplayCue, Language, ProductInfo } from "@expert/contentful";

import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import type { EligibilityMeta, EligibleProduct, SmartLinkMeta } from "../types";

import { getIsProd } from "../config";
import { EligibilityStatus, SaleProgress, SalesProvider } from "../types";
import { defaultEligibilityMeta, normalizeEligibilityMeta } from "./useProductOfferStore.helpers";

type ProductState = {
    products: EligibleProduct[];
    selectedProduct: EligibleProduct | undefined;
    /**
     * The offer id the expert originally selected, set when an eligibility re-check re-pointed
     * `selectedProduct` at a new id for the same SKU. Lets the send-link path log that it sent the
     * refreshed quoteId rather than the one the expert picked. Cleared on an explicit selection.
     */
    replacedSelectedProductOfferId?: string;
    /**
     * Every offer id a re-check moved `selectedProduct` off. Writes still keyed by one of them (an
     * Enroll or send-link request that was in flight across the re-check, or its WebSocket event) are
     * redirected to the live selection instead of being dropped. Kept while the selection stays on
     * the same SKU; cleared when it moves to another product.
     */
    supersededSelectedOfferIds?: string[];
    displayCues?: DisplayCue[];
};

/** Maps an offer id the selection was moved off to the live selected id; any other id is unchanged. */
const resolveOfferId = (productState: ProductState | undefined, productId: string) =>
    productState?.selectedProduct && productState.supersededSelectedOfferIds?.includes(productId)
        ? productState.selectedProduct.id
        : productId;

/** A re-check that moved the selected product to a new offer id (USP quoteId) for the same SKU. */
export type SelectedOfferReplacement = {
    productSku: string;
    previousProductOfferId: string;
    productOfferId: string;
};

type SmartLinkState = Record<string, SmartLinkMeta>;
/** productOfferId -> failed `/v2/enrollment` attempts. Named (not inline) to match SmartLinkState
 *  and to keep the immer draft type from tripping TS2589 on the store interface. */
type EnrollmentAttemptState = Record<string, number>;

interface ProductOfferStore {
    productStateBySession: Record<string, ProductState>;
    eligibilityMetaBySession: Record<string, EligibilityMeta>;
    smartLinkMetaBySession: Record<string, SmartLinkState>;
    /**
     * Failed `/v2/enrollment` attempts per (session, productOfferId) — the budget behind the Enroll
     * attempt cap (ew#7570). Keyed by productOfferId, not sessionId alone, because a session can
     * carry several eligible primary products and a capped offer must not retire Enroll for an
     * unrelated one (same keying bug #7123 fixed in useOfferChecklistStore and #7412 fixed for
     * smart-link remainingAttempts). Lives here rather than in component state so the cap survives
     * the step unmounting and, via `partialize`, a reload — `useIntegratedFlowStore` persists the
     * step, so a local counter would come back empty and re-arm Enroll on the same customer.
     */
    failedEnrollmentAttemptsBySession: Record<string, EnrollmentAttemptState>;

    getProductState: (sessionId: string) => ProductState;
    getProducts: (sessionId: string) => EligibleProduct[];
    getPrimaryProducts: (sessionId: string) => EligibleProduct[];
    getOtherProducts: (sessionId: string) => EligibleProduct[];
    getSelectedProduct: (sessionId: string) => EligibleProduct | undefined;
    getSessionIdForProductId: (productId: string) => string | undefined;
    /** The live offer id for `productId`: the selected id if a re-check moved the selection off it. */
    resolveProductOfferId: (sessionId: string, productId: string) => string;
    getEligibilityMetaBySessionId: (sessionId: string) => EligibilityMeta;
    getSmartLinkMetaBySessionId: (sessionId: string, productOfferId: string) => SmartLinkMeta;
    getFailedEnrollmentAttempts: (sessionId: string, productOfferId: string) => number;
    getDisplayCues: (sessionId: string) => DisplayCue[] | undefined;
    getReplacedSelectedProductOfferId: (sessionId: string) => string | undefined;
    /** Returns the selected-offer replacement this update made, if any, so callers can log it. */
    updateProducts: (
        sessionId: string,
        product: EligibleProduct[],
        displayCues?: DisplayCue[],
    ) => SelectedOfferReplacement | undefined;
    updateProduct: (
        sessionId: string,
        productId: string,
        change: { saleProgress?: SaleProgress; status?: EligibilityStatus; isConfettiShown?: boolean },
    ) => void;
    updateProductsConfettiStatus: (sessionId: string) => void;
    updateSelectedProduct: (sessionId: string, product: EligibleProduct | undefined) => void;
    updateEligibilityMetaBySessionId: (
        sessionId: string,
        eligibilityMeta: EligibilityMeta | Partial<EligibilityMeta>,
    ) => void;
    updateSmartLinkMetaBySessionId: (sessionId: string, productOfferId: string, smartLinkMeta: SmartLinkMeta) => void;
    /** Records one failed enrollment attempt and returns the new total for that offer. */
    incrementFailedEnrollmentAttempts: (sessionId: string, productOfferId: string) => number;

    isPrimaryProductInProgress: (sessionId: string) => boolean;
    getProductInfo: (sessionId: string, sku?: string, lang?: Language) => ProductInfo | undefined;
    resetSaleSession: (sessionId: string) => void;
    endSaleSession: (sessionId: string) => void;
}

export const useProductOfferStore = createWithEqualityFn<ProductOfferStore>()(
    persist(
        immer(
            devtools(
                (set, get) => ({
                    productStateBySession: {},
                    eligibilityMetaBySession: {},
                    smartLinkMetaBySession: {},
                    failedEnrollmentAttemptsBySession: {},
                    updateProducts: (sessionId, products, displayCues) => {
                        let replacement: SelectedOfferReplacement | undefined;
                        set(
                            (state) => {
                                const currentProductState = state.getProductState(sessionId);
                                const existingProducts = currentProductState.products;

                                // try to maintain sales progress state of the current products when setting new products
                                const updatedProducts = products.map((newProduct) => {
                                    // Match by id first, then fall back to productSku: ASP can return the
                                    // same offer (same SKU) under a new id on a re-check, and an id-only
                                    // match would drop the in-progress sale state onto the floor.
                                    const matchingExistingProduct =
                                        existingProducts.find(
                                            (existingProduct) => existingProduct.id === newProduct.id,
                                        ) ??
                                        existingProducts.find(
                                            (existingProduct) => existingProduct.productSku === newProduct.productSku,
                                        );

                                    // if no match, do not alter the incoming product
                                    if (!matchingExistingProduct) {
                                        return newProduct;
                                    }

                                    // if match, carry over the client-side sale state: sale progress and
                                    // whether confetti already fired (preserving saleProgress without
                                    // isConfettiShown would re-fire the sale confetti on every re-check).
                                    return {
                                        ...newProduct,
                                        saleProgress: matchingExistingProduct.saleProgress,
                                        isConfettiShown: matchingExistingProduct.isConfettiShown,
                                    };
                                });

                                // Keep the selected product on the live offer. A re-check (page reload,
                                // or an eligibility input changing mid-call) can return the selected SKU
                                // under a new id — for expert-sales that is a new USP quote, and USP
                                // invalidates the old one. Leaving `selectedProduct` on the old id made
                                // send-link call updateQuotes with a dead quote and 500 on every retry.
                                // Guards:
                                //   - Only expert-sales (USP). ASP offers are not invalidated on a re-check,
                                //     and its enrollment status is tracked on the offer id the link went
                                //     out on, so its selection is left where the expert put it.
                                //   - Only an in-progress sale. A finished one has nothing left to send,
                                //     and re-pointing would make useQuerySelectedProductOffer poll the new
                                //     id, whose (session-level) status could overwrite the outcome shown.
                                //   - Only to an *eligible* fresh offer: an ineligible re-check is handled
                                //     by the caller (useCollectCustomerInfo deliberately keeps the current
                                //     selection on the first ineligible address).
                                const { selectedProduct } = currentProductState;
                                let { replacedSelectedProductOfferId, supersededSelectedOfferIds } =
                                    currentProductState;
                                let nextSelectedProduct = selectedProduct;
                                if (
                                    selectedProduct?.salesProvider === SalesProvider.EXPERT_SALES &&
                                    selectedProduct.saleProgress === SaleProgress.InProgress &&
                                    !updatedProducts.some((product) => product.id === selectedProduct.id)
                                ) {
                                    // Same provider too: on a partner whose ASP eligibility gets the
                                    // unfiltered list, an ASP item can share the SKU.
                                    const freshSelected = updatedProducts.find(
                                        (product) =>
                                            product.productSku === selectedProduct.productSku &&
                                            product.salesProvider === selectedProduct.salesProvider,
                                    );
                                    if (freshSelected?.status === EligibilityStatus.Eligible) {
                                        nextSelectedProduct = freshSelected;
                                        // Keep the id the expert originally picked across repeat re-checks.
                                        replacedSelectedProductOfferId ??= selectedProduct.id;
                                        supersededSelectedOfferIds = [
                                            ...(supersededSelectedOfferIds ?? []),
                                            selectedProduct.id,
                                        ];
                                        replacement = {
                                            productSku: freshSelected.productSku,
                                            previousProductOfferId: selectedProduct.id,
                                            productOfferId: freshSelected.id,
                                        };
                                        // The send-link limit and the Enroll cap (ew#7570) are keyed by offer
                                        // id and must survive a reload; a new quote for the same sale must not
                                        // hand the expert a fresh budget, so carry both onto the new id.
                                        const smartLinkMeta = state.smartLinkMetaBySession[sessionId];
                                        const previousSmartLinkMeta = smartLinkMeta?.[selectedProduct.id];
                                        if (previousSmartLinkMeta && !smartLinkMeta[freshSelected.id]) {
                                            smartLinkMeta[freshSelected.id] = { ...previousSmartLinkMeta };
                                        }
                                        const failedAttempts = state.failedEnrollmentAttemptsBySession[sessionId];
                                        const previousFailedAttempts = failedAttempts?.[selectedProduct.id];
                                        if (previousFailedAttempts) {
                                            failedAttempts[freshSelected.id] = Math.max(
                                                failedAttempts[freshSelected.id] ?? 0,
                                                previousFailedAttempts,
                                            );
                                        }
                                    }
                                }

                                (state.productStateBySession[sessionId] as ProductState) = {
                                    ...currentProductState,
                                    products: updatedProducts,
                                    selectedProduct: nextSelectedProduct,
                                    replacedSelectedProductOfferId,
                                    supersededSelectedOfferIds,
                                    displayCues,
                                };
                            },
                            false,
                            "updateProducts",
                        );
                        return replacement;
                    },
                    updateProduct: (sessionId, productId, change) => {
                        set(
                            (state) => {
                                const productState = state.getProductState(sessionId);
                                const { products, selectedProduct } = productState;
                                if (!products) {
                                    return;
                                }
                                // An Enroll in flight across a re-check reports on the id it started with.
                                const targetId = resolveOfferId(productState, productId);

                                // `id` stays the matched entry's own: callers pass the whole product as the
                                // change, which after a redirect would carry the superseded id back in.
                                const newPrimaryProducts = products.map((currentProduct) =>
                                    currentProduct.id === targetId
                                        ? { ...currentProduct, ...change, id: currentProduct.id }
                                        : currentProduct,
                                );

                                const newSelectedProduct =
                                    selectedProduct?.id === targetId
                                        ? { ...selectedProduct, ...change, id: selectedProduct.id }
                                        : selectedProduct;

                                (state.productStateBySession[sessionId] as ProductState) = {
                                    ...state.productStateBySession[sessionId],
                                    products: newPrimaryProducts,
                                    selectedProduct: newSelectedProduct,
                                };
                            },
                            false,
                            "updateProduct",
                        );
                    },
                    updateProductsConfettiStatus: (sessionId) =>
                        set(
                            (state) => {
                                const currentProductState = state.getProductState(sessionId);
                                const updatedProducts = currentProductState.products.map((product) => {
                                    if (product.saleProgress === SaleProgress.Success && !product.isConfettiShown) {
                                        return { ...product, isConfettiShown: true };
                                    }
                                    return product;
                                });
                                (state.productStateBySession[sessionId] as ProductState) = {
                                    ...currentProductState,
                                    products: updatedProducts,
                                };
                            },
                            false,
                            "updateProductsConfettiStatus",
                        ),
                    updateSelectedProduct: (sessionId, product) =>
                        set(
                            (state) => {
                                const currentProductState = state.getProductState(sessionId);
                                (state.productStateBySession[sessionId] as ProductState) = {
                                    ...currentProductState,
                                    selectedProduct: product,
                                    // An explicit selection is current by definition.
                                    replacedSelectedProductOfferId: undefined,
                                    // Re-selecting the same offer (useCollectCustomerInfo after a re-check) can
                                    // still have a request in flight on a superseded id; another product cannot.
                                    // Same SKU *and* provider: an ASP item can share a USP item's SKU.
                                    supersededSelectedOfferIds:
                                        product &&
                                        product.productSku === currentProductState.selectedProduct?.productSku &&
                                        product.salesProvider === currentProductState.selectedProduct?.salesProvider
                                            ? currentProductState.supersededSelectedOfferIds
                                            : undefined,
                                };
                            },
                            false,
                            "updateSelectedProduct",
                        ),
                    updateEligibilityMetaBySessionId: (sessionId, eligibilityMeta) => {
                        set(
                            (state) => {
                                state.eligibilityMetaBySession[sessionId] = normalizeEligibilityMeta(eligibilityMeta);
                            },
                            false,
                            "updateEligibilityMeta",
                        );
                    },
                    updateSmartLinkMetaBySessionId: (sessionId, productOfferId, smartLinkMeta) => {
                        // A send-link response that landed after a re-check records against the live offer.
                        // Resolved outside the draft: reading the immer draft type here trips TS2589.
                        const targetId = get().resolveProductOfferId(sessionId, productOfferId);
                        set(
                            (state) => {
                                if (!state.smartLinkMetaBySession[sessionId]) {
                                    state.smartLinkMetaBySession[sessionId] = {};
                                }
                                state.smartLinkMetaBySession[sessionId][targetId] = smartLinkMeta;
                            },
                            false,
                            "updateSmartLinkMeta",
                        );
                    },
                    incrementFailedEnrollmentAttempts: (sessionId, productOfferId) => {
                        // A failed Enroll in flight across a re-check counts against the live offer.
                        const targetId = get().resolveProductOfferId(sessionId, productOfferId);
                        set(
                            (state) => {
                                if (!state.failedEnrollmentAttemptsBySession[sessionId]) {
                                    state.failedEnrollmentAttemptsBySession[sessionId] = {};
                                }
                                const bucket = state.failedEnrollmentAttemptsBySession[sessionId];
                                bucket[targetId] = (bucket[targetId] ?? 0) + 1;
                            },
                            false,
                            "incrementFailedEnrollmentAttempts",
                        );
                        // Read back the committed value rather than returning a locally-computed one, so
                        // the caller's "did this attempt hit the cap" decision can never disagree with
                        // what the store holds (zustand `set` is synchronous).
                        return get().getFailedEnrollmentAttempts(sessionId, targetId);
                    },
                    isPrimaryProductInProgress: (sessionId) => {
                        return (
                            get()
                                .getPrimaryProducts(sessionId)
                                ?.some((p) => p.saleProgress === SaleProgress.InProgress) ?? false
                        );
                    },
                    getProductState: (sessionId) => {
                        return (
                            get().productStateBySession[sessionId] ?? {
                                products: [],
                                selectedProduct: undefined,
                            }
                        );
                    },
                    getProducts: (sessionId) => {
                        return get().getProductState(sessionId).products;
                    },
                    getPrimaryProducts: (sessionId) =>
                        get()
                            .getProductState(sessionId)
                            .products.filter((p) => p.isPrimaryProduct) ?? [],
                    getOtherProducts: (sessionId) =>
                        get()
                            .getProductState(sessionId)
                            .products.filter((p) => !p.isPrimaryProduct) ?? [],
                    getSelectedProduct: (sessionId) => {
                        return get().getProductState(sessionId).selectedProduct;
                    },
                    getSessionIdForProductId: (productId) => {
                        return Object.keys(get().productStateBySession).find((sessionId) => {
                            const productState = get().getProductState(sessionId);
                            return (
                                productState.products.some((product) => product.id === productId) ||
                                // A WebSocket event for an offer the selection was moved off.
                                resolveOfferId(productState, productId) !== productId
                            );
                        });
                    },
                    resolveProductOfferId: (sessionId, productId) =>
                        resolveOfferId(get().productStateBySession[sessionId], productId),
                    getEligibilityMetaBySessionId: (sessionId) => {
                        return get().eligibilityMetaBySession[sessionId] ?? { ...defaultEligibilityMeta };
                    },
                    getProductInfo: (sessionId, sku, lang = "en-US") => {
                        return get()
                            .productStateBySession[sessionId]?.products.filter((p) => p.productSku === sku)
                            .at(0)?.productInfo?.[lang];
                    },
                    getSmartLinkMetaBySessionId: (sessionId, productOfferId) => {
                        const sessionMeta = get().smartLinkMetaBySession[sessionId];
                        if (!sessionMeta) {
                            return { maximumSmartLinksGenerated: false };
                        }
                        return sessionMeta[productOfferId] ?? { maximumSmartLinksGenerated: false };
                    },
                    // Returns a primitive, so unlike getSmartLinkMetaBySessionId there is no
                    // empty-session referential-stability concern (CLAUDE.md > Referential Stability).
                    getFailedEnrollmentAttempts: (sessionId, productOfferId) =>
                        get().failedEnrollmentAttemptsBySession[sessionId]?.[productOfferId] ?? 0,
                    getDisplayCues: (sessionId) => {
                        return get().productStateBySession?.[sessionId]?.displayCues;
                    },
                    getReplacedSelectedProductOfferId: (sessionId) =>
                        get().productStateBySession[sessionId]?.replacedSelectedProductOfferId,
                    resetSaleSession: (sessionId) =>
                        set(
                            (state) => {
                                delete state.productStateBySession[sessionId];
                                delete state.smartLinkMetaBySession[sessionId];
                                delete state.failedEnrollmentAttemptsBySession[sessionId];
                            },
                            false,
                            "resetSaleSession",
                        ),
                    endSaleSession: (sessionId) =>
                        set(
                            (state) => {
                                delete state.productStateBySession[sessionId];
                                delete state.smartLinkMetaBySession[sessionId];
                                delete state.eligibilityMetaBySession[sessionId];
                                delete state.failedEnrollmentAttemptsBySession[sessionId];
                            },
                            false,
                            "endSaleSession",
                        ),
                }),
                {
                    enabled: !getIsProd(),
                    store: "product-offer-store",
                    name: "workspace/sales-tools",
                },
            ),
        ),
        {
            name: "product-offer-store",
            // Skipped, but manually called elsewhere: void useProductOfferStore.persist.rehydrate();
            skipHydration: true,
            partialize: (state) => ({
                productStateBySession: state.productStateBySession,
                eligibilityMetaBySession: state.eligibilityMetaBySession,
                smartLinkMetaBySession: state.smartLinkMetaBySession,
                failedEnrollmentAttemptsBySession: state.failedEnrollmentAttemptsBySession,
            }),
        },
    ),
    shallow,
);
